<template>
  <div class="setting-bar">
    <el-select
      v-model="templateId"
      v-loadmore="loadMoreTemplate"
      class="m-r-10"
      @change="selectTemplate"
    >
      <el-option
        v-for="item in templateList"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
      <!-- 此处加载中的value可以随便设置，只要不与其他数据重复即可 -->
      <el-option v-if="templateMore" disabled label="加载中..." value="-1" />
    </el-select>
    <a-button
      class="m-r-10"
      type="primary"
      @click="openSetMaterialColumn"
    >字段列设置</a-button>

    <!-- 设置字段列弹窗 -->
    <a-modal
      v-model="setMaterialColumnVisible"
      title="字段列设置"
      width="1186px"
      :mask-closable="false"
    >
      <set-material-column ref="materialColumn" :column="columnConfig" />
      <template v-slot:footer>
        <a-button @click="handleCancelConfig">取消</a-button>
        <a-button
          type="primary"
          ghost
          @click="openSaveTemplateDialog"
        >另存为模板</a-button>
        <a-button type="primary" @click="handleConfirmConfig">确定</a-button>
      </template>
    </a-modal>

    <!-- 另存品目字段模板弹窗 -->
    <a-modal
      v-model="templateFormVisible"
      title="创建模板"
      width="550px"
      :mask-closable="false"
      @ok="saveAsTemplate"
    >
      <el-form
        :model="templateForm"
        :rules="templateFormRules"
        label-suffix="："
        label-width="120px"
      >
        <el-form-item label="模板名称" prop="templateName">
          <el-input
            v-model="templateForm.templateName"
            placeholder="请输入"
            maxlength="20"
          />
        </el-form-item>
        <el-form-item label="模板归属" prop="purchaseType">
          <el-select
            v-model="templateForm.purchaseType"
            placeholder="请选择"
            style="width: 100%"
            disabled
          >
            <el-option
              v-for="item in materialTemplateBelongDict.array"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
      </el-form>
    </a-modal>
  </div>
</template>

<script>
import SetMaterialColumn from '@/components/common/PurchaseListComponent/SetMaterialColumn.vue'
import { requiredRule } from '@/utils/form-validate'
import {
  materialTemplateBelongDict,
  materialTemplateStatusDict
} from '@/dictionary/material/template'
import {
  getMaterialTemplateApi,
  getMaterialTemplatePageApi,
  publishMaterialTemplateApi
} from '@/api/material/template'
import { cloneDeep } from 'lodash'
import materialColumnMixin from '@/components/common/PurchaseListComponent/material-column-mixin'

export default {
  name: 'PurchaseListTemplateSetting',

  components: { SetMaterialColumn },

  mixins: [materialColumnMixin],

  props: {
    // 是否只读
    readOnly: {
      type: Boolean,
      default: true
    },

    // 不选择默认模板
    noDefault: {
      type: Boolean,
      default: false
    },

    itemSchemaList: {
      type: Array,
      default: () => []
    },

    // 采购类型，目前支持询比和谈判采购
    purchaseType: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      // 模板id
      templateId: '',
      templateIdCopy: '',
      // 模板列表
      templateList: [],
      templatePage: 1,
      templateSize: 10,
      templateTotal: 0,
      templateMore: true,
      // 控制字段列设置弹窗显示
      setMaterialColumnVisible: false,
      // 控制模板保存表单弹窗显示
      templateFormVisible: false,
      // 模板保存表单
      templateForm: {
        // 模板名称
        templateName: '',
        // 模板归属
        purchaseType: ''
      },
      templateFormRules: {
        templateName: requiredRule('请填写'),
        purchaseType: requiredRule('请选择')
      },
      materialTemplateBelongDict
    }
  },

  watch: {
    itemSchemaList: {
      handler(val) {
        if (val?.length > 0) {
          this.setColumnConfig(val)
        }
      },
      deep: true,
      immediate: true
    },

    purchaseType: {
      handler(val) {
        if (val) this.templateForm.purchaseType = val
      },
      immediate: true
    }
  },

  mounted() {
    if (!this.readOnly) this.getTemplateList()
  },

  methods: {
    /**
     * 请求下一页模板数据
     */
    loadMoreTemplate() {
      // 如果没有更多数据，则不请求
      if (!this.templateMore) {
        return
      }

      // 如果intercept属性为true则不请求数据
      if (this.loadMoreTemplate.intercept) {
        return
      }
      this.loadMoreTemplate.intercept = true
      this.templatePage += 1
      this.getTemplateList({
        page: this.templatePage,
        more: true
      }).then(() => {
        this.loadMoreTemplate.intercept = false
      })
    },

    /**
     * 获取模板列表
     */
    getTemplateList({ page = 1, more = false } = {}) {
      return new Promise(resolve => {
        const params = {
          page: page,
          size: this.templateSize,
          purchaseType: this.purchaseType,
          templateStatus: materialTemplateStatusDict.ENABLE.value
        }
        getMaterialTemplatePageApi(params)
          .then(res => {
            const _data = res.data || {}
            this.templateTotal = _data.total || 0
            const records = (_data.records || []).map(item => {
              return {
                label: item.templateName,
                value: item.templateId,
                isDefault: item.isDefault,
                templateStatus: item.templateStatus
              }
            })
            this.templateMore = page * this.templateSize < this.templateTotal
            if (more) {
              this.templateList = this.templateList.concat(records)
            } else {
              this.templateList = records
              // 如果传入的模板配置有值，则不执行下面的操作
              if (this.noDefault) {
                return
              }
              // 默认选择第一项作为默认模板
              const defaultTemplate = this.templateList[0]
              this.templateId = defaultTemplate.value
              this.templateIdCopy = this.templateId
              this.getTemplateById()
            }
            resolve()
          })
          .catch(err => {
            console.error('获取品目模板列表失败', err)
            if (err?.message) this.$message.error(err.message)
          })
      })
    },

    /**
     * 根据模板id获取模板详情
     */
    async getTemplateById() {
      try {
        const RES = await getMaterialTemplateApi(this.templateId)
        const DEFINE_LIST = RES.data?.defineList || []
        this.setColumnConfig(DEFINE_LIST)
        this.$emit('getColumnConfig', this.columnConfig)
      } catch (err) {
        console.error('获取模板详情失败', err)
        if (err?.message) this.$message.error(err.message)
      }
    },

    async selectTemplate() {
      try {
        // 切换模板前提示数据会清空
        const BOOL = await this.$systemConfirm(
          '切换模板后，当前采购清单数据会被清空，请确认操作',
          '操作提示'
        )
        if (!BOOL) {
          this.templateId = this.templateIdCopy
          return
        }
        this.templateIdCopy = this.templateId
        await this.getTemplateById()
      } catch (e) {
        //
      }
    },

    /**
     * 打开字段列设置弹窗
     */
    openSetMaterialColumn() {
      this.setMaterialColumnVisible = true
    },

    /**
     * 另存为模板
     */
    openSaveTemplateDialog() {
      // 打开模板保存表单
      this.templateFormVisible = true
    },

    /**
     * 取消修改模板配置列表
     */
    handleCancelConfig() {
      this.setMaterialColumnVisible = false
      // 重置用外部的原始字段配置列表重置内部已修改的信息
      this.$refs.materialColumn.setFormTable(this.columnConfig)
    },

    /**
     * 确定修改模板配置列表
     */
    handleConfirmConfig() {
      // 同步字段列设置组件中的信息
      this.columnConfig = cloneDeep(this.$refs.materialColumn.updateColumn())
      this.setMaterialColumnVisible = false
      this.setTableConfig()
      this.$emit('getColumnConfig', this.columnConfig)
    },

    /**
     * 确认另存为模板
     */
    saveAsTemplate() {
      // 同步字段列设置组件中的信息
      const array = this.$refs.materialColumn.updateColumn()
      const postData = {
        purchaseType: this.templateForm.purchaseType,
        templateName: this.templateForm.templateName,
        defineList: array.map(item => {
          return {
            columnName: item.columnName,
            defineType: item.defineType,
            defineCode: item.defineCode,
            supplierRequire: item.supplierRequire,
            supplierVisible: item.supplierVisible,
            isEnable: item.enable ? 1 : 0,
            columnType: item.columnType
          }
        })
      }
      publishMaterialTemplateApi(postData)
        .then(() => {
          this.$message.success('模板发布成功')
          this.templateFormVisible = false
          this.getTemplateList()
        })
        .catch(err => {
          console.error('品目模板发布失败', err)
          if (err?.message) this.$message.error(err.message)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.setting-bar {
  display: inline-block;
}
</style>
